import { Skeleton } from '@mui/material'
import { AppV1Appsowner } from 'c1gen/roles'
import { C1StyledLink, C1StyledLinkProps } from 'components/core/links/C1Link'
import { useHasRole } from 'components/no-ui/capability'
import { TaskValue } from 'lib/actions/task'
import { SystemAnyAccessEntitlementID } from 'lib/constants/system'
import { routes, Route } from 'lib/routes'
import { ActionServiceGetRequest } from 'pbts/c1/api/action/v1/action'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { getActionApi } from 'redux_toolkit/actions'

type TaskTitleProps = {
    task: TaskValue
    stepUpTxn?: string
}

export const TaskTitleFormattedMessage: FC<TaskTitleProps> = ({ task }) => {
    if (!task.type?.taskType) {
        return <FormattedMessage id="components_task_dialog_unknown_task" defaultMessage="Unknown task type" />
    }

    switch (task.type?.taskType.oneofKind) {
        case 'grant':
            return <GrantTaskTitleFormattedMessage task={task} />
        case 'revoke':
            return <RevokeTaskTitleFormattedMessage task={task} />
        case 'certify':
            return <CertifyTaskTitleFormattedMessage task={task} />
        case 'action':
            return <ActionTaskTitleFormattedMessage task={task} />
        case 'offboarding':
            return <FormattedMessage id="components_task_dialog_offboarding_task" defaultMessage="Offboarding task" />
        default:
            return <FormattedMessage id="components_task_dialog_unknown_task" defaultMessage="Unknown task type" />
    }
}

/**
 * A subtle link variant that inherits color and only shows for app owners.
 * Uses type-safe routing via C1StyledLink.
 */
export const SubtleLink: FC<Omit<C1StyledLinkProps, 'href'> & { href: Route; 'data-dd-action-name'?: string }> = ({ children, 'data-dd-action-name': actionName, sx, ...props }) => {
    const isAppOwner = useHasRole(AppV1Appsowner)

    return isAppOwner
        ? <C1StyledLink variant="inherit" data-dd-action-name={actionName} sx={{ color: 'inherit', ...sx }} {...props}>{children}</C1StyledLink>
        : <>{children}</>
}

export const GrantTaskTitleFormattedMessage: FC<TaskTitleProps> = ({ task }) => {
    const userName = task.user?.displayName || task.identityUser?.displayName || task.appUser?.displayName
    const isExtension = task.type?.taskType.oneofKind === 'grant' && task.type.taskType.grant.source?.isExtension

    if (task.appEntitlements?.[0]?.id === SystemAnyAccessEntitlementID) {
        if (isExtension) {
            return <FormattedMessage
                id="components_task_dialog_grant_task_extension_grant_app_access"
                defaultMessage="Extension: <userLink>{subjectUserDisplayName}</userLink> needs <entitlementLink>application access</entitlementLink> to <appLink>{appDisplayName}</appLink>"
                values={{
                    userLink: (chunks) => <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>,
                    subjectUserDisplayName: userName,
                    appDisplayName: task.app?.displayName,
                    entitlementLink: (chunks) => <SubtleLink href={routes.applications.entitlement(task.appEntitlements?.[0]?.appId || '', task.appEntitlements?.[0]?.id || '')}>{chunks}</SubtleLink>,
                    appLink: (chunks) => <SubtleLink href={routes.applications.detail(task.app?.id || '')}>{chunks}</SubtleLink>,
                }}
            />
        }
        return <FormattedMessage
            id="components_task_dialog_grant_task_grant_app_access"
            defaultMessage="<userLink>{subjectUserDisplayName}</userLink> needs <entitlementLink>application access</entitlementLink> to <appLink>{appDisplayName}</appLink>"
            values={{
                userLink: (chunks) => <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>,
                subjectUserDisplayName: userName,

                entitlementLink: (chunks) => <SubtleLink href={routes.applications.entitlement(task.appEntitlements?.[0]?.appId || '', task.appEntitlements?.[0]?.id || '')}>{chunks}</SubtleLink>,

                appLink: (chunks) => <SubtleLink href={routes.applications.detail(task.app?.id || '')}>{chunks}</SubtleLink>,
                appDisplayName: task.app?.displayName,
            }}
        />
    }

    if (isExtension) {
        return <FormattedMessage
            id="components_task_dialog_grant_task_extension"
            defaultMessage="Extension: <userLink>{subjectUserDisplayName}</userLink> needs <entitlementLink>{entitlementDisplayName}</entitlementLink> in <appLink>{appDisplayName}</appLink>"
            values={{
                userLink: (chunks) => <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>,
                subjectUserDisplayName: userName,
                entitlementLink: (chunks) => <SubtleLink href={routes.applications.entitlement(task.appEntitlements?.[0]?.appId || '', task.appEntitlements?.[0]?.id || '')}>{chunks}</SubtleLink>,
                entitlementDisplayName: task.appEntitlements?.[0]?.displayName,
                appLink: (chunks) => <SubtleLink href={routes.applications.detail(task.app?.id || '')}>{chunks}</SubtleLink>,
                appDisplayName: task.app?.displayName,
            }}
        />
    }

    return <FormattedMessage
        id="components_task_dialog_grant_task"
        defaultMessage="<userLink>{subjectUserDisplayName}</userLink> needs <entitlementLink>{entitlementDisplayName}</entitlementLink> in <appLink>{appDisplayName}</appLink>"
        values={{
            userLink: (chunks) => <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>,
            subjectUserDisplayName: userName,
            entitlementLink: (chunks) => <SubtleLink href={routes.applications.entitlement(task.appEntitlements?.[0]?.appId || '', task.appEntitlements?.[0]?.id || '')}>{chunks}</SubtleLink>,
            entitlementDisplayName: task.appEntitlements?.[0]?.displayName,
            appLink: (chunks) => <SubtleLink href={routes.applications.detail(task.app?.id || '')}>{chunks}</SubtleLink>,
            appDisplayName: task.app?.displayName,
        }}
    />
}

export const RevokeTaskTitleFormattedMessage: FC<TaskTitleProps> = ({ task }) => {
    const userName = task.user?.displayName || task.identityUser?.displayName || task.appUser?.displayName

    if (task.appEntitlements?.[0]?.id === SystemAnyAccessEntitlementID) {
        return <FormattedMessage
            id="components_task_dialog_revoke_task_app_access"
            defaultMessage="Revoke <userLink>{subjectUserDisplayName}</userLink>'s access to <appLink>{appDisplayName}</appLink> app"
            values={{
                userLink: (chunks) => <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>,
                subjectUserDisplayName: userName,
                appLink: (chunks) => <SubtleLink href={routes.applications.detail(task.app?.id || '')}>{chunks}</SubtleLink>,
                appDisplayName: task.app?.displayName,
            }}
        />
    }

    return <FormattedMessage
        id="components_task_dialog_revoke_task"
        defaultMessage="Revoke <userLink>{subjectUserDisplayName}</userLink>'s access to <entitlementLink>{entitlementDisplayName}</entitlementLink> in <appLink>{appDisplayName}</appLink>"
        values={{
            userLink: (chunks) => <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>,
            subjectUserDisplayName: userName,
            entitlementLink: (chunks) => <SubtleLink href={routes.applications.entitlement(task.appEntitlements?.[0]?.appId || '', task.appEntitlements?.[0]?.id || '')}>{chunks}</SubtleLink>,
            entitlementDisplayName: task.appEntitlements?.[0]?.displayName,
            appLink: (chunks) => <SubtleLink href={routes.applications.detail(task.app?.id || '')}>{chunks}</SubtleLink>,
            appDisplayName: task.app?.displayName,
        }}
    />
}

export const CertifyTaskTitleFormattedMessage: FC<TaskTitleProps> = ({ task }) => {
    const userName = task.user?.displayName || task.identityUser?.displayName || task.appUser?.displayName

    if (task.appEntitlements?.[0]?.id === SystemAnyAccessEntitlementID) {
        return <FormattedMessage
            id="components_task_dialog_certify_task_app_access"
            defaultMessage="Review <userLink>{subjectUserDisplayName}</userLink>'s access to <appLink>{appDisplayName}</appLink> app"
            values={{
                userLink: (chunks) => <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>,
                subjectUserDisplayName: userName,
                appLink: (chunks) => <SubtleLink href={routes.applications.detail(task.app?.id || '')}>{chunks}</SubtleLink>,
                appDisplayName: task.app?.displayName,
            }}
        />
    }

    return <FormattedMessage
        id="components_task_dialog_certify_task"
        defaultMessage="Review <userLink>{subjectUserDisplayName}</userLink>'s access to <entitlementLink>{entitlementDisplayName}</entitlementLink> in <appLink>{appDisplayName}</appLink>"
        values={{
            userLink: (chunks) => (task.user
                ? <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>
                : <SubtleLink href={routes.applications.account(task.app?.id || '', task.appUser?.id || '')} data-dd-action-name="View app user">{chunks}</SubtleLink>),
            subjectUserDisplayName: userName,

            entitlementLink: (chunks) => <SubtleLink href={routes.applications.entitlement(task.appEntitlements?.[0]?.appId || '', task.appEntitlements?.[0]?.id || '')}>{chunks}</SubtleLink>,
            entitlementDisplayName: task.appEntitlements?.[0]?.displayName,

            appLink: (chunks) => <SubtleLink href={routes.applications.detail(task.app?.id || '')}>{chunks}</SubtleLink>,
            appDisplayName: task.app?.displayName,
        }}
    />
}

export const ActionTaskTitleFormattedMessage: FC<TaskTitleProps> = ({ task }) => {
    const actionId = task.type?.taskType.oneofKind === 'action' ? task.type.taskType.action.actionId : undefined

    const { data: action, isLoading } = getActionApi.useGet(
        ActionServiceGetRequest.create({ id: actionId || '' }),
        { actionable: !!actionId },
    )

    if (isLoading) {
        return <Skeleton variant="text" width={300} />
    }

    if (action?.displayName) {
        return <>
            {action.displayName}
            {' '}
            <FormattedMessage id="components_task_dialog_action" defaultMessage="action" />
        </>
    }

    const userName = task.user?.displayName || task.identityUser?.displayName || task.appUser?.displayName

    if (userName) {
        return <FormattedMessage
            id="components_task_dialog_action_task"
            defaultMessage="Action task for <userLink>{subjectUserDisplayName}</userLink>"
            values={{
                userLink: (chunks) => (task.user
                    ? <SubtleLink href={routes.users.detail(task.user?.id || '')} data-dd-action-name="View user">{chunks}</SubtleLink>
                    : task.appUser
                        ? <SubtleLink href={routes.applications.account(task.app?.id || '', task.appUser?.id || '')} data-dd-action-name="View app user">{chunks}</SubtleLink>
                        : <>{chunks}</>),
                subjectUserDisplayName: userName,
            }}
        />
    }

    return <FormattedMessage
        id="components_task_dialog_action_task_no_subject"
        defaultMessage="Action task"
    />
}
